/**
 * Zone Impact Assessment
 *
 * Calculates the impact of zone inconsistencies on lap times and
 * generates actionable insights for riders, driven by the defensible
 * consistency-% model (share-within-tolerance) rather than raw variance
 * heuristics — see consistencyPercent.ts.
 */

import {
  type TrackZoneType,
  type ZoneSeverity,
  type ZoneImpact,
  type ZoneVarianceMetrics,
  type ZoneConsensusMetrics,
} from './TrackZoneTypes'
import { type ZoneDataPoint } from './ZoneDetection'
import {
  type ZoneConsistencyPercent,
  type PercentMetricKey,
  pctBand,
} from '../sessionAnalysis/zoneDisplay/referencePointConsistency/consistencyPercent'
import { MIN_WORTH_MS } from '../config/focusZones'

// ============================================================================
// Impact Calculation
// ============================================================================

/** Calculate impact assessment for a zone */
export function calculateZoneImpact(
  zoneType: TrackZoneType,
  variance: ZoneVarianceMetrics,
  consensus: ZoneConsensusMetrics,
  zoneLength: number,
  percent: ZoneConsistencyPercent | null,
  zoneDataPoints?: ZoneDataPoint[][],
  /**
   * Per-lap zone times measured on the recalibrated frame (pass 25).
   * When present (>=2 entries) they replace the odometer-frame
   * estimateZoneTimeMs integration as the input to the median-vs-fastest
   * estimate; the estimate logic itself is unchanged.
   */
  actualZoneTimesMs?: number[]
): ZoneImpact {
  const estimatedTimeLossMs = estimateTimeLoss(
    variance,
    consensus,
    zoneLength,
    zoneDataPoints,
    actualZoneTimesMs
  )
  const severity = determineSeverity(percent)
  const insight = generatePercentInsight(percent, estimatedTimeLossMs, zoneType)

  return {
    estimatedTimeLossMs,
    severity,
    rank: 0, // Set during sorting in main analysis
    insight,
  }
}

/**
 * Estimate zone time for a single lap from its data points.
 * Integrates distance/speed across consecutive points.
 */
function estimateZoneTimeMs(dataPoints: ZoneDataPoint[]): number {
  if (dataPoints.length < 2) return 0
  let totalTimeMs = 0
  for (let i = 0; i < dataPoints.length - 1; i++) {
    const dist = dataPoints[i + 1].distance - dataPoints[i].distance
    if (dist <= 0) continue
    // Average speed between consecutive points (km/h → m/s)
    const avgSpeedMs = (dataPoints[i].speed + dataPoints[i + 1].speed) / 2 / 3.6
    if (avgSpeedMs > 0) {
      totalTimeMs += (dist / avgSpeedMs) * 1000
    }
  }
  return totalTimeMs
}

function estimateTimeLoss(
  variance: ZoneVarianceMetrics,
  consensus: ZoneConsensusMetrics,
  zoneLength: number,
  zoneDataPoints?: ZoneDataPoint[][],
  actualZoneTimesMs?: number[]
): number {
  // Preferred: actual recalibrated zone times (pass 25); otherwise integrate
  // from data points on the odometer frame as before.
  const zoneTimes =
    actualZoneTimesMs && actualZoneTimesMs.length >= 2
      ? actualZoneTimesMs
      : (zoneDataPoints ?? []).map(estimateZoneTimeMs).filter((t) => t > 0)

  if (zoneTimes.length >= 2) {
    // Use median vs fastest to estimate typical time loss.
    // This aligns with consistency: if most laps are similar (high consistency),
    // the median will be close to the fastest → small time loss. One outlier
    // lap won't inflate the number, avoiding contradictions like "79% consistent
    // but -0.75s time loss." The zone detail view still shows per-lap deltas
    // so the rider can see the full range there.
    const sorted = [...zoneTimes].sort((a, b) => a - b)
    const fastestTime = sorted[0]
    const medianTime = sorted[Math.floor(sorted.length / 2)]
    const timeLoss = Math.round(medianTime - fastestTime)
    if (timeLoss > 0) return timeLoss
  }

  // Fallback: speed-based estimate
  const avgSpeed = (consensus.avgEntrySpeed + consensus.avgExitSpeed) / 2
  if (avgSpeed <= 0) return 0

  const entryStdDev = variance.details?.entrySpeedVariance?.stdDev ?? 0
  const exitStdDev = variance.details?.exitSpeedVariance?.stdDev ?? 0
  const avgSpeedStdDevKmh = (entryStdDev + exitStdDev) / 2

  if (avgSpeedStdDevKmh <= 0) {
    const zoneTimeMs = (zoneLength / (avgSpeed / 3.6)) * 1000
    return Math.round(zoneTimeMs * variance.speedCV * 0.5)
  }

  const avgSpeedMs = avgSpeed / 3.6
  const speedSpreadMs = (avgSpeedStdDevKmh * 2) / 3.6
  const fastSpeedMs = avgSpeedMs + speedSpreadMs / 2
  const slowSpeedMs = Math.max(avgSpeedMs - speedSpreadMs / 2, 1)
  const fastTimeMs = (zoneLength / fastSpeedMs) * 1000
  const slowTimeMs = (zoneLength / slowSpeedMs) * 1000

  return Math.round(slowTimeMs - fastTimeMs)
}

/**
 * Severity runs on the honest number: the band of the lowest row's raw pct
 * (pass 14). A one-off can raise severity — the attribution line names the
 * lap, and the honest row % is what the racer sees.
 */
function determineSeverity(
  percent: ZoneConsistencyPercent | null
): ZoneSeverity {
  if (!percent || percent.status !== 'ok' || !percent.lowest) return 'low'
  const band = pctBand(percent.lowest.pct)
  if (band === 'red') return 'high'
  if (band === 'amber') return 'medium'
  return 'low'
}

/** Format time saved as a short note, empty if negligible */
function getTimeSavedNote(ms: number): string {
  if (ms < MIN_WORTH_MS) return ''
  return ` Worth ~${(ms / 1000).toFixed(1)}s.`
}

// ============================================================================
// Insight Generation
// ============================================================================

/**
 * Action-closing copy: the second sentence in each insight, prompting the rider
 * on where to focus. Every PercentMetricKey MUST have a non-empty entry here
 * (enforced by ZoneImpact.test.ts iterating ALL_KEYS). Binding product copy — byte-exact.
 */
export const ACTION_CLOSERS: Record<PercentMetricKey, string> = {
  brakePoint: 'Same brake marker every lap — once consistent, try adjusting.',
  brakeRelease:
    'Let go at the same point each lap so corner entry stays repeatable.',
  entrySpeed: 'Settle it earlier so you arrive at the same speed every time.',
  minSpeed: 'Ride the same line so the speed at your slowest point repeats.',
  minSpeedLocation:
    'Same line every lap, then experiment with the slowest point.',
  exitSpeed: 'Get the throttle pickup consistent first.',
  throttlePoint: 'Pick a reference and open at the same spot each lap.',
  speedCurve: 'Smooth that stretch first.',
  time: 'Check the chart for where laps diverge.',
}

/**
 * Static leads: the opening sentence when spread cannot be quantified (rounds to 0
 * or stats are missing). Every PercentMetricKey MUST have a non-empty entry here
 * (enforced by ZoneImpact.test.ts iterating ALL_KEYS). Binding product copy — byte-exact.
 */
// NOTE: STATIC_LEADS.speedCurve is provably unreachable via the
// `?? STATIC_LEADS[lead.metric]` fallback below — quantifiedLead() always
// returns a non-null diverge% string for 'speedCurve' (never falls through
// to the static lead). The entry is required anyway because this Record is
// typed exhaustively over PercentMetricKey; harmless, not a bug — see pass-16
// minors sweep item 22.
export const STATIC_LEADS: Record<PercentMetricKey, string> = {
  brakePoint: 'Your brake point wanders lap to lap.',
  brakeRelease: 'Brake release varies lap to lap.',
  entrySpeed: 'Entry speed swings between laps.',
  minSpeed: 'Mid-corner speed varies between laps.',
  minSpeedLocation: 'Your slowest point wanders lap to lap.',
  exitSpeed: 'Exit speed varies between laps.',
  throttlePoint: 'Throttle pickup moves around.',
  speedCurve:
    'Lap speeds diverge across this zone — the dashed band shows the worst stretch.',
  time: 'Zone time varies more than your reference points do.',
}

/** Dismissal copy for a zone that's already consistent — no action template. */
export const CONSISTENT_INSIGHT =
  'Consistent through here — change one thing at a time and see what the data says.'

/**
 * Format ±spread per unit; null when stats missing or it rounds to 0.
 * A "±0" sentence is worse than the static lead.
 */
function spreadValue(row: {
  stats: { typicalSpread: number } | null
  unit: string
}): string | null {
  if (!row.stats) return null
  if (row.unit === 'ms') {
    const s = (row.stats.typicalSpread / 1000).toFixed(1)
    return s === '0.0' ? null : s
  }
  const s = Math.round(row.stats.typicalSpread)
  return s === 0 ? null : String(s)
}

/**
 * Quantified lead: ±spread formatted per metric, or null if unquantifiable.
 * speedCurve uses diverge%, others use ±spread.
 */
function quantifiedLead(row: {
  metric: PercentMetricKey
  pct: number
  stats: { typicalSpread: number } | null
  unit: string
}): string | null {
  if (row.metric === 'speedCurve')
    return `Lap speeds diverge over ${100 - row.pct}% of this zone — the dashed band shows the worst stretch.`
  const s = spreadValue(row)
  if (s === null) return null
  const leads: Record<Exclude<PercentMetricKey, 'speedCurve'>, string> = {
    brakePoint: `Your brake point moves ±${s} m lap to lap.`,
    brakeRelease: `Brake release varies ±${s} m lap to lap.`,
    entrySpeed: `Entry speed swings ±${s} km/h between laps.`,
    minSpeed: `Mid-corner speed varies ±${s} km/h between laps.`,
    minSpeedLocation: `Your slowest point wanders ±${s} m lap to lap.`,
    exitSpeed: `Exit speed varies ±${s} km/h between laps.`,
    throttlePoint: `Throttle pickup moves ±${s} m lap to lap.`,
    time: `Zone time varies ±${s}s beyond your reference points.`,
  }
  return leads[row.metric as Exclude<PercentMetricKey, 'speedCurve'>]
}

/**
 * Secondary clause: appended to cover additional failing metrics.
 * Null if unquantifiable (stats missing or rounds to 0), or time (never a secondary).
 */
function secondaryClause(row: {
  metric: PercentMetricKey
  pct: number
  stats: { typicalSpread: number } | null
  unit: string
}): string | null {
  if (row.metric === 'speedCurve')
    return `Lap speeds also diverge over ${100 - row.pct}% of the zone.`
  const s = spreadValue(row)
  if (s === null) return null
  const clauses: Record<Exclude<PercentMetricKey, 'speedCurve'>, string> = {
    brakePoint: `Brake point also moves ±${s} m.`,
    brakeRelease: `Release point also varies ±${s} m.`,
    entrySpeed: `Entry speed also swings ±${s} km/h.`,
    minSpeed: `Mid-corner speed also varies ±${s} km/h.`,
    minSpeedLocation: `The slowest point also wanders ±${s} m.`,
    exitSpeed: `Exit speed also varies ±${s} km/h.`,
    throttlePoint: `Throttle pickup also moves ±${s} m.`,
    time: '', // never reached — time is filtered out of secondaries
  }
  return clauses[row.metric as Exclude<PercentMetricKey, 'speedCurve'>] || null
}

/**
 * Generate the zone-detail insight from the consistency-% model: quantified
 * multi-metric composition with causal merging in braking zones.
 * Falls back to the zone-type copy when the % model has no data (insufficient
 * laps / not applicable), and to the dismissal copy when the zone is already consistent.
 */
export function generatePercentInsight(
  percent: ZoneConsistencyPercent | null,
  timeLossMs: number,
  zoneType: TrackZoneType
): string {
  if (!percent || percent.status !== 'ok') {
    return generateFallbackInsight(zoneType)
  }
  if (percent.isConsistent || !percent.lowest) {
    return CONSISTENT_INSIGHT
  }
  let failing = percent.rows
    .filter((r) => r.nature === 'habit')
    .sort((a, b) => a.actionPct - b.actionPct)
  // Time aggregates the other rows: it leads only when it is the SOLE failing row.
  if (failing.length > 1) failing = failing.filter((r) => r.metric !== 'time')

  // One-off-only inconsistency (pass 14): raw pct can fail while every row's
  // nature is 'one-off' (actionPct recovered). There is no habit to quantify —
  // fall back to the zone-type copy, same as the no-data path.
  if (failing.length === 0) {
    return generateFallbackInsight(zoneType)
  }

  const parts: string[] = []
  let leadMetric: PercentMetricKey
  const bp = failing.find((r) => r.metric === 'brakePoint')
  const es = failing.find((r) => r.metric === 'entrySpeed')
  const sBp = bp ? spreadValue(bp) : null
  const sEs = es ? spreadValue(es) : null
  let consumed: { metric: PercentMetricKey }[]
  if (zoneType === 'braking' && bp && es && sBp !== null && sEs !== null) {
    parts.push(
      `Your brake point moves ±${sBp} m, swinging entry speed by ±${sEs} km/h.`
    )
    leadMetric = 'brakePoint'
    consumed = [bp, es]
  } else {
    const lead = failing[0]
    parts.push(quantifiedLead(lead) ?? STATIC_LEADS[lead.metric])
    leadMetric = lead.metric
    consumed = [lead]
  }
  const secondaries = failing
    .filter((r) => !consumed.includes(r))
    .map(secondaryClause)
    .filter((c): c is string => c !== null)
    .slice(0, 2)
  parts.push(...secondaries)
  parts.push(ACTION_CLOSERS[leadMetric])
  return parts.join(' ') + getTimeSavedNote(timeLossMs)
}

function generateFallbackInsight(zoneType: TrackZoneType): string {
  const fallbacks: Record<TrackZoneType, string> = {
    braking:
      'Braking varies between laps. Same brake marker every lap — once repeatable, try adjusting.',
    cornering:
      'Corner approach varies between laps. Same line every lap — once consistent, experiment to find time.',
    acceleration:
      'Exit varies between laps. Same throttle point every lap — once repeatable, try rolling on earlier.',
    straight:
      'Some variation detected — usually linked to how the previous corner is exited.',
  }

  return fallbacks[zoneType]
}
